import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import ProfilePanel from "@components/templates/Profile/ProfilePanel/ProfilePanel";
import { profileTabs, profileTypeEnum } from "@components/templates/Profile/ProfileHeader/profile-enum";
import { getEventName, getUserActivePlan } from "@helpers/profileHelper";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import { generateTrackingObject } from "@core/global/global-country-functions";
import { isBuyerSupplier, companyEvents, companyTabs, panelData, portProfileAPIEndPoints } from "@containers/CompanyProfile/utils/enum.js";
import { renderCompanyPanelData, renderCompanyProfileTabsAltUI } from "@containers/CompanyProfile/utils/helper";
import { companyProfileTypeHandler, tabDetailsHandler } from "@redux/CompanyProfile/companyProfileSlice";
import axios from "axios";
import * as Sentry from "@sentry/browser";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { tabHeadingDictionary } from "@core/global/global-country-dictionary";

const ProfileDetails = ({ isSalesForce, panelList, setPanelList, isProfileViewExhaust, isStarterPlan, handleNudgePopup }) => {
  const { countDetails, tabDetails } = useSelector((state) => state.companyProfile);
  const { activePanelId, activeTabId } = tabDetails;
  const [companyType, setCompanyType] = useState(null);
  const dispatch = useDispatch();
  const { reverseRelation } = countDetails;
  const { search: searchUrlParams, pathname } = useLocation();
  const { companyID, companyname } = useParams();
  const { loading } = countDetails;
  const profileName = new URLSearchParams(searchUrlParams).get("tab");
  const dataSource = new URLSearchParams(searchUrlParams).get("dataSource");
  const subTab = new URLSearchParams(searchUrlParams).get("subTab");
  // Consume subTab only once on initial mount so it doesn't keep overriding tab clicks
  const subTabAppliedRef = useRef(false);

  useEffect(() => {
    if (!isStarterPlan && handleNudgePopup) {
      handleNudgePopup("due_diligence_reports", "event");
    }
    if (profileName) {
      setCompanyType(profileName);
      dispatch(companyProfileTypeHandler(profileName));
    } else {
      let reqObj = {
        isSubsidiaryIncluded: true,
        map_type: "trade_country",
        profile_type: "company",
        slug: companyname,
        sub_category: "overview",
        _id: companyID,
      };
      axios
        .post(portProfileAPIEndPoints.getProfileHeaderDetails, reqObj)
        .then(({ data: { data } }) => {
          setCompanyType(data.isBuyer ? "I" : "E");
          dispatch(companyProfileTypeHandler(data.isBuyer ? "I" : "E"));
        })
        .catch((err) => {
          Sentry.captureException(err);
        });
    }
  }, [profileName]);

  useEffect(() => {
    const activePanelTab = panelData.find((item) => item.redirectionEnum === companyType);
    const filteredTabs = [];
    let tabsData = activePanelTab?.length ? activePanelTab : companyTabs;
    tabsData.forEach((tab) => {
      if ((tab.countEnum && countDetails[tab.countEnum] > 0) || tab.isExcludeFilter) {
        filteredTabs.push({ ...tab, count: countDetails[tab.countEnum] });
      }
    });
    if (activePanelTab) {
      // Do NOT apply subTab here — let the profile load normally so CompanyOverview
      // mounts and populates countDetails. subTab is applied after countDetails loads.
      dispatch(
        tabDetailsHandler({
          ...tabDetails,
          activePanelId: activePanelTab.id,
          activeTab: activePanelTab?.tabs ? activePanelTab?.tabs : null,
          activeTabId: filteredTabs[0]?.id,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchUrlParams, companyType]);

  useEffect(() => {
    if (!panelList?.length && Object.keys(countDetails).length > 1) {
      let panelDataList = panelData.filter((item) => countDetails[item?.key] !== false);
      setPanelList(panelDataList);
      if (countDetails[isBuyerSupplier.isBuyer] === false) {
        handlePanelClick(tabHeadingDictionary.exporters);
      }
    }
  }, [reverseRelation, companyType, countDetails]);

  // Apply subTab (e.g. trading_partner from workspace "View All") once activePanelId is set.
  // Runs independently so it's not blocked by Object.keys guard or cache state.
  useEffect(() => {
    if (!subTab || subTabAppliedRef.current || !activePanelId) return;
    const activePanelTab = panelData.find((item) => item.id === activePanelId);
    const panelHasSubTab = activePanelTab && (activePanelTab?.tabs || []).some((t) => t.id === subTab);
    if (panelHasSubTab) {
      subTabAppliedRef.current = true;
      dispatch(tabDetailsHandler({ ...tabDetails, activeTabId: subTab }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePanelId, subTab]);

  const handlePanelClick = (id, countEnum = "") => {
    let selectedPanel = panelData.find((panel) => panel.id === id);
    if (id === "contact_details") {
      trademoTrackingEvents(trackingEventDictionary.companyProfilePageRequestContactDetailsTab, generateTrackingObject(dataSource, pathname, getUserActivePlan()));
    } else if (id === "due_diligence_reports") {
      trademoTrackingEvents(trackingEventDictionary.companyProfileDueDiligenceReportTab, generateTrackingObject(dataSource, pathname, getUserActivePlan()));
    } else {
      trademoTrackingEvents(getEventName(profileTypeEnum.company, companyEvents.overview[countEnum]), generateTrackingObject(dataSource, pathname, getUserActivePlan()));
    }
    const filteredTabs = [];
    let tabsData = selectedPanel?.tabs?.length ? selectedPanel?.tabs : companyTabs;
    tabsData.forEach((tab) => {
      if ((tab.countEnum && countDetails[tab.countEnum] > 0) || tab.isExcludeFilter) {
        filteredTabs.push({ ...tab, count: countDetails[tab.countEnum] });
      }
    });

    dispatch(
      tabDetailsHandler({
        ...tabDetails,
        activePanelId: id,
        activeTabId: id !== "importers" && id !== "exporters" ? (filteredTabs[0]?.id ? filteredTabs[0]?.id : selectedPanel?.tabs ? selectedPanel?.tabs?.[0]?.id : profileTabs[0].id) : filteredTabs[1]?.id ? filteredTabs[1]?.id : selectedPanel?.tabs ? selectedPanel?.tabs?.[1]?.id : profileTabs[0].id,
        activeTab: selectedPanel?.tabs ? selectedPanel?.tabs : null,
      })
    );
  };

  return panelData && panelData.length >= 0 ? (
    <>
      <div className={`${isStarterPlan ? "tw-relative tw-w-[calc(100vw-240px)] tw-flex tw-justify-center tw-flex-col" : ""} ${isProfileViewExhaust ? "tw-h-[calc(100vh-250px)]" : ""}`}>
        <ProfilePanel panelData={panelList} activePanelId={activePanelId} handlePanelClick={handlePanelClick} loading={panelList?.length ? false : loading} loadingWidth={"15rem"} />
        {/* one can remove the below comment  */}
        {/* activePanelId === 'due_diligence_reports' or 'corporate_heirarchy' */}
        {isStarterPlan && (activePanelId === "due_diligence_reports" || activePanelId === "corporate_heirarchy") ? <div className="tw-h-[calc(100vh-270px)]">{renderCompanyProfileTabsAltUI(activePanelId)}</div> : <div className={`${["vocc", "nvocc", "freight_forwarder", "hs_code"].includes(activeTabId) || ["contact_details"].includes(activePanelId) ? "tw-pl-3" : ""}`}>{renderCompanyPanelData(activePanelId, countDetails, isSalesForce, handlePanelClick)}</div>}
      </div>
    </>
  ) : null;
};

export default ProfileDetails;
